/** Implements specific event handlers for drag, drop, and swipe interactions on task items. */
// @ts-check
import { SWIPE_ACTION_THRESHOLD_PX } from '@/config/constants';
import { isMainTask } from '@/features/tasks/components/atoms/taskMetadataViewState';
import { applyFlatListReorderMove } from '@/features/tasks/utils/interactions/flatListReorderApply';

const KNOWN_LIST_PREFIXES = [
  'overdue-list',
  'overdue',
  'next-action',
  'unscheduled',
  'next-actions',
  'upcoming',
];

/**
 * dnd-kit ids may be prefixed (`grid-`, `next-action:`) when the same task
 * is rendered in more than one list. Prefer the task carried on drag data.
 * @param {{ id?: string|number, data?: { current?: { task?: { id?: string } } } }} item
 * @returns {string|null}
 */
export const resolveDndTaskId = (item) => {
  const fromData = item?.data?.current?.task?.id;
  if (fromData) return fromData;
  let raw = String(item?.id ?? '');
  if (raw.startsWith('grid-')) raw = raw.slice(5);
  if (raw.startsWith('tli-disabled-')) raw = raw.slice('tli-disabled-'.length);
  for (const prefix of KNOWN_LIST_PREFIXES) {
    if (raw.startsWith(`${prefix}:`)) return raw.slice(prefix.length + 1);
  }
  return raw || null;
};

/**
 * @param {Function} handleToggleComplete
 * @param {Function} updateTask
 * @param {string} userId
 */
const createSwipeHandler =
  (handleToggleComplete, updateTask, userId) => (active, event) => {
    /** @type {DndActive} */
    const dndActive = active;
    const deltaX = event.delta.x;
    const taskId = String(dndActive.id).replace('swipe-', '');
    if (deltaX > SWIPE_ACTION_THRESHOLD_PX) {
      handleToggleComplete(dndActive.data.current.task, true);
    } else if (deltaX < -SWIPE_ACTION_THRESHOLD_PX) {
      const leftSwipeAction = dndActive.data.current.leftSwipeAction;
      const date =
        leftSwipeAction === 'today'
          ? new Date()
          : (() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return tomorrow;
            })();
      updateTask(taskId, { date }, userId);
    }
  };

/**
 * A hoofdtaak (top-level task with subtasks) never carries a clock time — dropping
 * it on an hour-grid slot only assigns the date (deadline semantics), the time is
 * dropped. A standalone task without subtasks is a normal schedulable task and
 * gets the real time.
 * @param {Function} updateTask
 * @param {string} userId
 * @param {Map<string, Task>} tasksMap
 */
const createCalendarSlotHandler =
  (updateTask, userId, tasksMap) => (active, overData) => {
    /** @type {DndData} */
    const data = overData;
    const taskId = resolveDndTaskId(active);
    if (!taskId) return;
    const task = tasksMap?.get(String(taskId));
    const payload = { date: data.date };
    if (!isMainTask(task, tasksMap)) payload.time = data.time;
    updateTask(taskId, payload, userId);
  };

/**
 * @param {Map<string, Task>} tasksMap
 * @param {Task[]} reorderList Flat list matching SortableContext (not hierarchical taskTree)
 * @param {(id: string, fields: object, userId: string) => void | Promise<void>} updateTask
 * @param {(id: string, fields: object, userId: string, task?: object) => void | Promise<void>} moveTask
 * @param {string} userId
 */
const createUnscheduledTaskHandler =
  (tasksMap, reorderList, updateTask, moveTask, userId) => (active, over) => {
    /** @type {DndActive} */
    const dndActive = active;
    /** @type {DndOver} */
    const dndOver = over;

    const activeId = resolveDndTaskId(dndActive);
    const activeTask = tasksMap.get(String(activeId));
    if (!activeTask) return;
    const overId = resolveDndTaskId(dndOver);
    applyFlatListReorderMove({
      reorderList,
      activeId,
      overId,
      activeTask,
      tasksMap,
      moveTask: moveTask || updateTask,
      updateTask,
      userId,
    });
  };

/**
 * @param {Function} updateTask
 * @param {string} userId
 */
const createUnscheduledListHandler = (updateTask, userId) => (active) => {
  const taskId = resolveDndTaskId(active);
  if (!taskId) return;
  updateTask(taskId, { date: null, time: null }, userId);
};

/**
 * @param {Function} handleToggleComplete
 * @param {(id: string, fields: object, userId: string) => void | Promise<void>} updateTask
 * @param {string} userId
 * @param {Map<string, Task>} tasksMap
 * @param {Task[]} reorderList Flat unscheduled/SortableContext list (not hierarchical tree)
 * @param {(id: string, fields: object, userId: string, task?: object) => void | Promise<void>} [moveTask] Routes orderKey via executeMoveTask (materializedPath + cascade)
 */
export const createDropHandlers = (
  handleToggleComplete,
  updateTask,
  userId,
  tasksMap,
  reorderList,
  moveTask,
) => ({
  handleSwipeableDrop: createSwipeHandler(
    handleToggleComplete,
    updateTask,
    userId,
  ),
  handleCalendarSlotDrop: createCalendarSlotHandler(updateTask, userId, tasksMap),
  handleUnscheduledTaskDrop: createUnscheduledTaskHandler(
    tasksMap,
    reorderList,
    updateTask,
    moveTask,
    userId,
  ),
  handleUnscheduledListDrop: createUnscheduledListHandler(updateTask, userId),
});

/** @param {DndData} overData */
const isCalendarSlot = (overData) =>
  overData?.droppable === true && overData?.date && overData?.time;
/** @param {DndData} overData */
const isUnscheduledTask = (overData) => overData?.type === 'unscheduled-task';
/** @param {string|number} overId */
const isUnscheduledList = (overId) =>
  overId === 'unscheduled-list' || overId === 'unscheduled-tasks-list';

const handleCalendarSlotDropWrapper = (
  isCalendarSlotFlag,
  handleCalendarSlotDrop,
  active,
  over,
) => {
  if (isCalendarSlotFlag) {
    return handleCalendarSlotDrop(active, over.data.current);
  }
};

const handleUnscheduledTaskDropWrapper = (
  isUnscheduledTaskFlag,
  handleUnscheduledTaskDrop,
  active,
  over,
) => {
  if (isUnscheduledTaskFlag) {
    return handleUnscheduledTaskDrop(active, over);
  }
};

const handleUnscheduledListDropWrapper = (
  isUnscheduledListFlag,
  handleUnscheduledListDrop,
  active,
) => {
  if (isUnscheduledListFlag) {
    return handleUnscheduledListDrop(active);
  }
};

/**
 * @param {Function} handleCalendarSlotDrop
 * @param {Function} handleUnscheduledTaskDrop
 * @param {Function} handleUnscheduledListDrop
 */
export const createRouteDropHandler =
  (
    handleCalendarSlotDrop,
    handleUnscheduledTaskDrop,
    handleUnscheduledListDrop,
  ) =>
  (active, over) => {
    /** @type {DndData} */
    const overData = over.data.current;

    handleCalendarSlotDropWrapper(
      isCalendarSlot(overData),
      handleCalendarSlotDrop,
      active,
      over,
    );
    handleUnscheduledTaskDropWrapper(
      isUnscheduledTask(overData),
      handleUnscheduledTaskDrop,
      active,
      over,
    );
    handleUnscheduledListDropWrapper(
      isUnscheduledList(over.id),
      handleUnscheduledListDrop,
      active,
    );
  };

/** @param {DndType} activeType */
const shouldIgnoreDragEnd = (activeType) =>
  activeType === 'daily-task' || activeType === 'next-action';

const handleSwipeableTaskDrop = (handleSwipeableDrop, active, event) => {
  if (shouldIgnoreDragEnd(active?.data?.current?.type)) return;
  handleSwipeableDrop(active, event);
};

/**
 * @param {Function} handleSwipeableDrop
 * @param {Function} routeDropHandler
 * @param {Function} handleDragCancel
 * @returns {(event: DndEvent) => void}
 */
export const createDragEndHandler =
  (handleSwipeableDrop, routeDropHandler, handleDragCancel) => (event) => {
    document.body.classList.remove('dnd-is-dragging');
    handleDragCancel();

    const { active, over } = event;
    const activeType = active?.data?.current?.type;

    if (shouldIgnoreDragEnd(activeType)) return;
    if (activeType === 'swipeable-task') {
      handleSwipeableTaskDrop(handleSwipeableDrop, active, event);
      return;
    }
    if (!over || active.id === over.id) return;

    routeDropHandler(active, over);
  };
